import React from 'react';
import { find, get, isEmpty } from 'lodash';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

// local dependencies
import { callDispositionCloseCall as CallDispositionCloseCallView } from 'ui/views/widgets/callDispositionCloseCall';
import { SERVICE_RESPONSE_STATUS, CALL_ACTIVITY_STATE, CALL_RESOLUTION_STATUS } from 'constants/common';
import { CallDispositionCloseCallService } from 'services/callDispositionCloseCall';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as errorOverlay from 'utils/errorOverlay';
import { clearCallState } from 'store/actions/callState';
import { REDUCER } from 'constants/entity-names';
import { DASHBOARD } from 'constants/route-paths';
import { messageBus } from 'core/modules/message-bus';
import * as MESSAGE_BUS_EVENTS from 'constants/message-bus-events';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { CallDispositionCloseCallWidget } from 'ui/containers/CallDispositionCloseCallWidget';
 *
 * // render
 * <CallDispositionCloseCallWidget propName='propValue'/>
 */
class _CallDispositionCloseCallWidget extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // bind events and methods to instance
        this.handleCallDataChange = this.handleCallDataChange.bind( this );
        this.handleCallClose = this.handleCallClose.bind( this );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,

            // call closing data
            callCloseData: {
                callResolution: ! isEmpty( props.autoTaggingCallResolution ) ? props.autoTaggingCallResolution : undefined,
                callerMood: undefined,
            }
        };

        // create service instance
        this.callDispositionCloseCallService = new CallDispositionCloseCallService();
        
        // start component data fetch
        this.fetchComponentData();
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'CallDispositionCloseCallWidget.fetchComponentData()' );

        // cancel ongoing request
        this.callDispositionCloseCallService.cancel();

        // get status and service data
        const { status, data } = await this.callDispositionCloseCallService.fetchComponentData( {
            url: this.props.widget.url,
            data: { patientId: this.props.patientId }
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
            } );
        }
    }

    /**
     * @desc Update call close data in the state.
     */
    handleCallDataChange( key, value ) {
        log.debug( 'CallDispositionCloseCallWidget.handleCallDataChange()' );

        this.setState( {
            callCloseData: {
                ...this.state.callCloseData,
                [ key ]: value,
            }
        } );
    }

    /**
     * @desc Update call close data in the state.
     */
    async handleCallClose() {
        log.debug( 'CallDispositionCloseCallWidget.handleCallClose()' );

        // get close call action
        const closeCallAction = find( this.state.componentData.actions, { name: 'closeCall' } );

        // cancel ongoing request
        this.callDispositionCloseCallService.cancel();

        // show page level preloader
        pageLevelPreloader.show();

        // get status and service data
        const { status } = await this.callDispositionCloseCallService.closeCall( {
            url: closeCallAction.url,
            data: this.state.callCloseData,
            patientId: this.props.patientId
        } );

        // hide page level preloader
        pageLevelPreloader.hide();

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
            }, () => {
                this.props.clearCallState(); // clear call state
                this.props.history.replace( DASHBOARD ); // move to dashboard

                messageBus.trigger( MESSAGE_BUS_EVENTS.CALL_ENDED );
                
            } );
        } else {
            errorOverlay.open(); // show error
        }
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.callDispositionCloseCallService.cancel(); // cancel ongoing requests
    }

    /**
     * @desc When prop changes, update state
     */
    UNSAFE_componentWillReceiveProps( newProps ) {
        if( newProps.autoTaggingCallResolution !== this.props.autoTaggingCallResolution ) {
            this.setState( {
                callCloseData: {
                    ...this.state.callCloseData,
                    callResolution: ! isEmpty( newProps.autoTaggingCallResolution ) ? newProps.autoTaggingCallResolution : undefined,
                }
            } );
        }
    }

    /**
     * @desc Render a React view component
     */
    render() {

        // do not display the widget unless the call has ended
        if( get( this.props, 'callState.callState' ) !== CALL_ACTIVITY_STATE.ENDED ) {
            return null;
        }

        // MSR can not select "resolved" if the auto-tagging resolution is "unresolved"
        const disableResolutionSelection = ! isEmpty( this.props.autoTaggingCallResolution ) && ( this.props.autoTaggingCallResolution === CALL_RESOLUTION_STATUS.UNRESOLVED );

        return (
            <CallDispositionCloseCallView
                className={ this.props.className }
                callReasonValidationState={ this.props.callReasonValidationState }
                callNotesValidationState={ this.props.callNotesValidationState }
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                callCloseData={ this.state.callCloseData }
                disableResolutionSelection={ disableResolutionSelection }
                onCallDataChange={ this.handleCallDataChange }
                onCallClose={ this.handleCallClose }
                callState={ get( this.props, 'callState', {} ) }
                { ...this.state.componentData }
            />
        );
    }
}

// set display name
_CallDispositionCloseCallWidget.displayName = '_CallDispositionCloseCallWidget';

// set default props
_CallDispositionCloseCallWidget.defaultProps = {};

// prop types
_CallDispositionCloseCallWidget.propTypes = {};

/*************************************************/

// map state to props
const mapStateToProps = ( state ) => {
    return {
        callState: state[ REDUCER.CALL_STATE ]
    };
};

// map dispatch to props
const mapDispatchToProps = ( dispatch ) => {
    return {
        clearCallState: bindActionCreators( clearCallState, dispatch ),
    };
};


// return component with acccess to Redux store
export const CallDispositionCloseCallWidget = withRouter( connect( mapStateToProps, mapDispatchToProps )( _CallDispositionCloseCallWidget ) );
